/**
 * Générateur — Convertir des durées
 * Attendu ID : 652 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, texte_trou_saisie, relier
 * Labels de référence :
 *   - Convertir une durée entre différentes unités de temps
 *   - Passer de l'écriture sexagésimale à l'écriture décimale et inversement
 *   - Effectuer une conversion en chaîne par division euclidienne
 * Variantes estimées : 3000+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 652;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONV = 'Convertir une durée entre différentes unités de temps';

// ─── Équivalences décimales / fractions ───

const EQUIVS = [
    { dec: 0.5,  frac: '1/2',  min: 30, label: 'une demi-heure' },
    { dec: 0.25, frac: '1/4',  min: 15, label: 'un quart d\'heure' },
    { dec: 0.75, frac: '3/4',  min: 45, label: 'trois quarts d\'heure' },
    { dec: 0.1,  frac: '1/10', min: 6,  label: 'un dixième d\'heure' },
];

// ─── Helpers ───

const pad = (n) => String(n).padStart(2, '0');

/** Formate une durée sexagésimale */
const fmtSexa = (h, m, s) => {
    if (s !== undefined && s > 0) return `${h} h ${pad(m)} min ${pad(s)} s`;
    if (m === 0) return `${h} h`;
    return `${h} h ${pad(m)} min`;
};

/** Formate une durée décimale */
const fmtDec = (val) => {
    if (Number.isInteger(val)) return `${val} h`;
    // Format avec virgule française
    return `${String(val).replace('.', ',')} h`;
};

// ─── Génération de données par sous-type ───

/**
 * Sous-type 1 : Conversion simple unité → unité
 * min→h, h→min, s→min, jours→h, semaines→jours, etc.
 */
const genSimple = () => {
    const conversions = [
        // min → h min
        () => {
            const totalMin = randInt(2, 8) * 60 + randInt(5, 55);
            const h = Math.floor(totalMin / 60);
            const m = totalMin % 60;
            return {
                // sousType directionnel (revue 2026-08-01) : vers la grande
                // unité = division — la fiche montre la potence, pas le ×
                sousType: 'simple_divi',
                question: `Convertir ${totalMin} minutes en heures et minutes.`,
                answer: fmtSexa(h, m),
                distracteurs: [
                    // « 1 h = 100 min » : l'élève découpe aux centaines (230 → 2 h 30).
                    // L'ancienne formule totalMin − h×100 mélangeait quotient par 60
                    // et paquets de 100 → minutes négatives (revue 2026-08-01).
                    fmtSexa(Math.floor(totalMin / 100), totalMin % 100),
                    fmtSexa(h + 1, m),
                    fmtSexa(h, m + 10 > 59 ? m - 10 : m + 10),
                ].filter(d => d !== fmtSexa(h, m)),
            };
        },
        // h min → min
        () => {
            const h = randInt(1, 5);
            const m = randInt(5, 55);
            const total = h * 60 + m;
            return {
                sousType: 'simple_multi',
                question: `Convertir ${fmtSexa(h, m)} en minutes.`,
                answer: `${total} min`,
                distracteurs: [
                    `${h * 100 + m} min`, // concaténation
                    `${total + 60} min`,
                    `${total - 10} min`,
                ].filter(d => d !== `${total} min`),
            };
        },
        // s → min s
        () => {
            const totalS = randInt(2, 12) * 60 + randInt(5, 55);
            const m = Math.floor(totalS / 60);
            const s = totalS % 60;
            return {
                sousType: 'simple_divi',
                question: `Convertir ${totalS} secondes en minutes et secondes.`,
                answer: `${m} min ${pad(s)} s`,
                distracteurs: [
                    // « 1 min = 100 s » : découpe aux centaines (305 → 3 min 05)
                    `${Math.floor(totalS / 100)} min ${pad(totalS % 100)} s`,
                    `${m + 1} min ${pad(s)} s`,
                    `${m} min ${pad(s > 30 ? s - 15 : s + 15)} s`,
                ].filter(d => d !== `${m} min ${pad(s)} s`),
            };
        },
        // h → min
        () => {
            const h = randInt(2, 10);
            const total = h * 60;
            return {
                sousType: 'simple_multi',
                question: `Combien de minutes y a-t-il dans ${h} heures ?`,
                answer: `${total} min`,
                distracteurs: [
                    `${h * 100} min`,
                    `${total + 30} min`,
                    `${total - 60} min`,
                ].filter(d => d !== `${total} min`),
            };
        },
        // jours → h
        () => {
            const j = randInt(2, 7);
            const total = j * 24;
            return {
                sousType: 'simple_multi',
                question: `Combien d'heures y a-t-il dans ${j} jours ?`,
                answer: `${total} h`,
                distracteurs: [
                    `${j * 12} h`,
                    `${total + 24} h`,
                    `${total - 12} h`,
                ].filter(d => d !== `${total} h`),
            };
        },
        // semaines → jours
        () => {
            const s = randInt(2, 8);
            const total = s * 7;
            return {
                sousType: 'simple_multi',
                question: `Combien de jours y a-t-il dans ${s} semaines ?`,
                answer: `${total} jours`,
                distracteurs: [
                    `${s * 5} jours`,
                    `${total + 7} jours`,
                    `${total - 3} jours`,
                ].filter(d => d !== `${total} jours`),
            };
        },
    ];

    return randomChoice(conversions)();
};

/**
 * Sous-type 2 : Conversion en chaîne (division euclidienne)
 * s → h min s, min → h min, h → sem j h
 */
const genChaine = () => {
    const types = [
        // secondes → h min s
        () => {
            const h = randInt(1, 12);
            const m = randInt(5, 55);
            const s = randInt(5, 55);
            const totalS = h * 3600 + m * 60 + s;
            return {
                question: `Convertir ${totalS} secondes en heures, minutes et secondes.`,
                answer: fmtSexa(h, m, s),
                steps: [
                    { op: `${totalS} ÷ 60`, q: Math.floor(totalS / 60), r: totalS % 60, unit_q: 'min', unit_r: 's' },
                    { op: `${Math.floor(totalS / 60)} ÷ 60`, q: h, r: m, unit_q: 'h', unit_r: 'min' },
                ],
                distracteurs: [
                    fmtSexa(h, m + 10 > 59 ? m - 10 : m + 10, s),
                    fmtSexa(h + 1, m, s),
                    `${Math.floor(totalS / 60)} min ${totalS % 60} s`, // s'arrête trop tôt
                ],
            };
        },
        // minutes → h min
        () => {
            const h = randInt(2, 15);
            const m = randInt(5, 55);
            const totalMin = h * 60 + m;
            return {
                question: `Convertir ${totalMin} minutes en heures et minutes.`,
                answer: fmtSexa(h, m),
                steps: [
                    { op: `${totalMin} ÷ 60`, q: h, r: m, unit_q: 'h', unit_r: 'min' },
                ],
                distracteurs: [
                    // « 1 h = 100 min » : découpe aux centaines (même fix que genSimple)
                    fmtSexa(Math.floor(totalMin / 100), totalMin % 100),
                    fmtSexa(h + 1, m),
                    fmtSexa(h - 1, m + 60),
                ],
            };
        },
        // heures → semaines jours heures
        () => {
            const sem = randInt(1, 5);
            const j = randInt(1, 6);
            const h = randInt(1, 23);
            const totalH = sem * 168 + j * 24 + h;
            return {
                question: `Convertir ${totalH} heures en semaines, jours et heures.`,
                answer: `${sem} sem ${j} j ${h} h`,
                steps: [
                    { op: `${totalH} ÷ 24`, q: sem * 7 + j, r: h, unit_q: 'j', unit_r: 'h' },
                    { op: `${sem * 7 + j} ÷ 7`, q: sem, r: j, unit_q: 'sem', unit_r: 'j' },
                ],
                distracteurs: [
                    `${sem} sem ${j + 1} j ${h} h`,
                    `${sem + 1} sem ${j} j ${h} h`,
                    `${sem * 7 + j} j ${h} h`, // s'arrête trop tôt
                ],
            };
        },
        // heures → jours heures
        () => {
            const j = randInt(2, 15);
            const h = randInt(1, 23);
            const totalH = j * 24 + h;
            return {
                question: `Convertir ${totalH} heures en jours et heures.`,
                answer: `${j} j ${h} h`,
                steps: [
                    { op: `${totalH} ÷ 24`, q: j, r: h, unit_q: 'j', unit_r: 'h' },
                ],
                distracteurs: [
                    `${j + 1} j ${h} h`,
                    `${j} j ${h + 12} h`,
                    `${j - 1} j ${h + 24} h`,
                ],
            };
        },
    ];

    return randomChoice(types)();
};

/**
 * Sous-type 3 : Sexagésimal ↔ Décimal
 * 2 h 30 min ↔ 2,5 h ; utilise les 4 équivalences clés
 */
const genSexaDec = () => {
    const eq = randomChoice(EQUIVS);
    const baseH = randInt(1, 8);
    const sexaStr = fmtSexa(baseH, eq.min);
    const decVal = baseH + eq.dec;
    const decStr = fmtDec(decVal);
    const fracStr = `${baseH} + ${eq.frac} h`;
    // 3e distracteur « fraction voisine » (revue 2026-08-01) : ses minutes
    // {30,15,45,6} et le piège centièmes {50,25,75,10} sont des ensembles
    // DISJOINTS — aucune collision possible par construction (l'ancien
    // « ±15/20 min » recréait « h 50 » = le piège, pour la demi-heure).
    const autre = randomChoice(EQUIVS.filter(e => e.min !== eq.min));

    const direction = randomChoice(['sexa_to_dec', 'dec_to_sexa']);

    if (direction === 'sexa_to_dec') {
        // Piège principal : lire les minutes comme des centièmes
        const wrongDec = fmtDec(baseH + eq.min / 100);
        return {
            sousType: 'sexadec_vers_dec',
            question: `Écrire ${sexaStr} en écriture décimale.`,
            answer: decStr,
            sexa: sexaStr,
            dec: decStr,
            frac: fracStr,
            distracteurs: [
                wrongDec !== decStr ? wrongDec : fmtDec(decVal + 0.5),
                fmtDec(decVal + 1),
                fmtDec(baseH + autre.dec),
            ].filter(d => d !== decStr),
        };
    } else {
        // Décimal → Sexagésimal
        const wrongMin = Math.round(eq.dec * 100); // piège : 0.5 → 50 min
        const wrongSexa = fmtSexa(baseH, wrongMin);
        return {
            sousType: 'sexadec_vers_sexa',
            question: `Écrire ${decStr} en heures et minutes.`,
            answer: sexaStr,
            sexa: sexaStr,
            dec: decStr,
            frac: fracStr,
            distracteurs: [
                wrongSexa !== sexaStr ? wrongSexa : fmtSexa(baseH, eq.min + 15),
                fmtSexa(baseH + 1, eq.min),
                fmtSexa(baseH, autre.min),
            ].filter(d => d !== sexaStr),
        };
    }
};

/**
 * Sous-type 4 : Fraction ↔ Minutes
 * 1/2 h = 30 min, 1/4 h = 15 min, etc.
 */
const genFracMin = () => {
    const eq = randomChoice(EQUIVS);
    const direction = randomChoice(['frac_to_min', 'min_to_frac']);

    if (direction === 'frac_to_min') {
        // Pool par construction (revue 2026-08-01) : lecture décimale
        // (0,5 → « 50 min ») + minutes de deux autres fractions. Aucune
        // valeur ne peut coïncider avec la clé — l'ancien « 60 − min »
        // VALAIT la bonne réponse pour la demi-heure, d'où le bouche-trou
        // « 30 min ? » qui partait en prod.
        const autres = shuffle(EQUIVS.filter(e => e.min !== eq.min)).slice(0, 2);
        return {
            sousType: 'frac_vers_min',
            // Le label parlé (« trois quarts d'heure ») plutôt que « 3/4
            // d'heure » : français naturel + l'élève traduit l'expression
            // orale en quantité (revue 2026-08-01).
            question: `Combien de minutes font ${eq.label} ?`,
            answer: `${eq.min} min`,
            distracteurs: [
                `${Math.round(eq.dec * 100)} min`,
                ...autres.map(e => `${e.min} min`),
            ],
        };
    } else {
        // Fractions en KaTeX \dfrac (revue 2026-08-01, précédent gen_622) —
        // en saisie, le builder de champs produit $\dfrac{{{1}}}{{{2}}}$,
        // le motif à trous dans la fraction déjà supporté (gen_621).
        const tex = (frac) => {
            const [n, d] = frac.split('/');
            return `$\\dfrac{${n}}{${d}}$ h`;
        };
        const autresFrac = EQUIVS.filter(e => e.frac !== eq.frac);
        return {
            sousType: 'min_vers_frac',
            question: `${eq.min} minutes, c'est quelle fraction d'heure ?`,
            answer: tex(eq.frac),
            distracteurs: [
                // Erreur-type « oubli de la conversion » (eq.min h) — cherry-pick
                // Yannis 2026-07-28. L'ancien distracteur `${eq.min}/60 h` valait
                // NUMÉRIQUEMENT la clé (30/60 h = 1/2 h) → deuxième réponse juste.
                `${eq.min} h`,
                tex(autresFrac[0].frac),
                tex(autresFrac[1].frac),
            ].filter(d => d !== tex(eq.frac)),
        };
    }
};

// ═══════════════════════════════════════════
// EXERCICE — conversion_duree
// ═══════════════════════════════════════════

// Moteurs à clés (revue 2026-08-01) : le sousType est DIRECTIONNEL quand le
// geste dépend de la branche (data.sousType posé par elle : simple_multi /
// simple_divi / sexadec_vers_dec / sexadec_vers_sexa / frac_vers_min /
// min_vers_frac) ; la clé moteur sert de repli ('chaine', toujours ÷).
// calculatrice true uniquement sur les chaînes (divisions lourdes type
// 44 523 s), les autres conversions se font de tête.
const MOTEURS = [
    { key: 'simple', gen: genSimple },
    { key: 'chaine', gen: genChaine },
    { key: 'sexadec', gen: genSexaDec },
    { key: 'fracmin', gen: genFracMin },
];

const poserMeta = (payload, key) => {
    payload.contenu = { ...payload.contenu, sousType: key, calculatrice: key === 'chaine' };
    payload._meta.generatorKey = 'conversion_duree';
    return payload;
};

const genConvQcm = () => {
    // Retirage complet tant qu'un tirage ne fournit pas 4 options distinctes :
    // on n'invente JAMAIS d'option à l'assemblage (règle skill A3, revue
    // 2026-08-01 — les anciens filets « correct + ' ?' » et horaire aléatoire
    // apparaissaient dans ~3 % des QCM). Les moteurs garantissent désormais
    // leurs 3 distracteurs par construction : le retirage est un vrai filet.
    for (let t = 0; t < 30; t++) {
        const moteur = randomChoice(MOTEURS);
        const data = moteur.gen();
        const correct = data.answer;
        const distracteurs = (data.distracteurs || [])
            .filter(d => d && d !== correct && !d.includes('NaN') && !d.includes('undefined'));
        const choices = [...new Set([correct, ...distracteurs])].slice(0, 4);
        if (choices.length < 4) continue;

        const texte = `<p>${data.question}</p>`;
        const payload = makeQcm(choices, correct, texte, opts(L_CONV));
        return poserMeta(payload, data.sousType || moteur.key);
    }
    throw new Error('gen_652 : 30 retirages sans 4 options distinctes — moteur à auditer');
};

const genConvTexte = () => {
    const moteur = randomChoice(MOTEURS);
    const data = moteur.gen();

    // Saisie strict de formes composées (« 2 sem 3 j 5 h » à la frappe
    // exacte) → champs numériques par unité, tolérants (revue 2026-08-01,
    // remède 650/651). Unités hors champ, collées en NBSP (mobile). Les clés
    // paddées (« 05 ») sont dépouillées : deux champs de même valeur doivent
    // rester identiques en texte (RT-VARIANTE-FAUSSE-VRAIE sinon). Une
    // fraction « 1/2 h » devient naturellement {{1}}/{{2}} h.
    const champs = data.answer
        .replace(/ /g, ' ')
        .replace(/\d+(?:,\d+)?/g, (x) => `{{${x.includes(',') ? x : String(parseInt(x, 10))}}}`);

    // Cas fraction : ligne ENTIÈREMENT KaTeX (motif 621) — hasMathLines exige
    // une ligne qui commence et finit par $ ; une ligne mixte « Réponse :
    // $...$ h. » s'affichait en LaTeX brut (retour utilisateur 2026-08-01).
    if (data.answer.includes('dfrac')) {
        const [, nfr, dfr] = data.answer.match(/\\dfrac\{(\d+)\}\{(\d+)\}/);
        // \rule invisibles (largeur 0) : profondeur sous le numérateur et
        // hauteur sur le dénominateur — sinon les champs de saisie superposés
        // recouvrent la barre de fraction (retour utilisateur 2026-08-01).
        const phrase = `$\\text{Réponse : } \\dfrac{{{${nfr}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${dfr}}}} \\text{ h}$`;
        const texte = `<p>${data.question} Écris la fraction la plus simple.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(L_CONV), mode: 'saisie', strict: false });
        return poserMeta(payload, data.sousType || moteur.key);
    }

    const texte = `<p>${data.question}</p>`;
    const payload = makeTexteTrou(`Réponse : ${champs}.`, texte, { ...opts(L_CONV), mode: 'saisie', strict: false });
    return poserMeta(payload, data.sousType || moteur.key);
};

const genConvTrou = () => {
    // Texte à trous guidé pour conversion en chaîne
    const chaineData = genChaine();

    // Champs par unité + insécables (revue 2026-08-01) : l'ancien
    // « Résultat : {{15 h 53 min}} » exigeait la frappe exacte de toute la
    // durée dans UN champ texte. Chaque nombre a désormais son champ, les
    // unités restent affichées hors champ, collées en NBSP (mobile).
    const nbEsp = '\u00a0';
    const etape = (s) => `${s.op} =${nbEsp}{{${s.q}}}${nbEsp}${s.unit_q} reste${nbEsp}{{${s.r}}}${nbEsp}${s.unit_r}.`;

    // Aération : une étape par ligne, le résultat sur sa propre ligne
    // (retour utilisateur 2026-08-01)
    let phrase;
    if (chaineData.steps.length === 2) {
        phrase = `${etape(chaineData.steps[0])}\nPuis ${etape(chaineData.steps[1])}`;
    } else {
        // Résultat recopié depuis les valeurs BRUTES de l'étape (pas la clé
        // paddée « 05 ») : deux trous de même valeur restent identiques en
        // texte, sinon le harnais voit un échange de champs accepté
        // (RT-VARIANTE-FAUSSE-VRAIE, seed 4347 itér 132).
        const s1 = chaineData.steps[0];
        phrase = `${etape(s1)}\nRésultat : {{${s1.q}}}${nbEsp}${s1.unit_q}${nbEsp}{{${s1.r}}}${nbEsp}${s1.unit_r}.`;
    }

    const texte = `<p>${chaineData.question} Complète les étapes de la conversion.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CONV), mode: 'saisie', strict: false });
    return poserMeta(payload, 'chaine');
};

const genConvRelier = () => {
    // Refonte bilan prof D3 « plus tard » (arbitré 2026-07-27) : l'ancien
    // 4 ↔ 4 sur deux heures différentes se triait par l'heure entière, et la
    // bijection offrait la dernière paire. Désormais :
    //   · MÊME heure pour les deux durées (plus aucun tri par l'entier) ;
    //   · gabarit 2 sources / 4 cibles : les 2 conversions correctes + la
    //     recopie_minutes de CHAQUE durée (« 2 h 12 → 2,12 h ») — la paire
    //     complète : l'élève recopieur retrouve SES deux écritures (cohérence
    //     anti-déduction, même logique que 624) ;
    //   · garde numérique par RETIRAGE : la recopie h,mm peut VALOIR la
    //     conversion correcte de l'autre durée (réel : recopie de 2 h 30 =
    //     « 2,30 h » = 2,3 h = conversion exacte de 2 h 18 — un élève juste
    //     pourrait défendre le lien croisé). Les 4 valeurs doivent être deux à
    //     deux distinctes NUMÉRIQUEMENT, pas seulement en texte.
    const MINS_DEC = [
        { min: 6, dec: 0.1 },
        { min: 12, dec: 0.2 },
        { min: 15, dec: 0.25 },
        { min: 18, dec: 0.3 },
        { min: 30, dec: 0.5 },
        { min: 36, dec: 0.6 },
        { min: 45, dec: 0.75 },
        { min: 48, dec: 0.8 },
    ];
    const recopieTxt = (h, min) => `${h},${pad(min)} h`;

    for (let t = 0; t < 60; t++) {
        const h = randInt(2, 6);
        const deux = shuffle([...MINS_DEC]).slice(0, 2);
        const valeurs = [
            ...deux.map(({ dec }) => h + dec),          // conversions correctes
            ...deux.map(({ min }) => h + min / 100),    // recopies des minutes
        ];
        if (new Set(valeurs.map(v => v.toFixed(4))).size !== 4) continue;

        const pairs = deux.map(({ min, dec }) => ({
            source: fmtSexa(h, min),
            target: fmtDec(h + dec),
        }));
        const leurres = deux.map(({ min }) => ({
            target: recopieTxt(h, min),
            code: 'recopie_minutes',
        }));

        const texte = '<p>Relie chaque durée à son écriture décimale, en heures. '
            + 'Attention : certaines écritures ne correspondent à aucune durée.</p>';
        const payload = makeRelier(pairs, texte, { ...opts(L_CONV), cibles_leurres: leurres });
        poserMeta(payload, 'sexadec_vers_dec');
        payload._meta.params = {
            h, minutes: deux.map(d => d.min),
            distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
        };
        return payload;
    }
    // Repli déterministe vérifié : 3 h 12 (3,2 h) et 3 h 45 (3,75 h) —
    // recopies 3,12 h et 3,45 h, quatre valeurs distinctes.
    const payload = makeRelier(
        [
            { source: fmtSexa(3, 12), target: fmtDec(3.2) },
            { source: fmtSexa(3, 45), target: fmtDec(3.75) },
        ],
        '<p>Relie chaque durée à son écriture décimale, en heures. '
        + 'Attention : certaines écritures ne correspondent à aucune durée.</p>',
        {
            ...opts(L_CONV),
            cibles_leurres: [
                { target: recopieTxt(3, 12), code: 'recopie_minutes' },
                { target: recopieTxt(3, 45), code: 'recopie_minutes' },
            ],
        });
    return poserMeta(payload, 'sexadec_vers_dec');
};

// ─── Registre ───
const GENERATORS = {
    conversion_duree_qcm_unique:        genConvQcm,
    conversion_duree_texte:             genConvTexte,
    conversion_duree_texte_trou_saisie: genConvTrou,
    conversion_duree_relier:            genConvRelier,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'conversion_duree', label: L_CONV, formatsList: buildFormats('conversion_duree') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
